import React, { useEffect, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import "../../styles/pages/subpages/news_page.css";
import "../../styles/pages/subpages/news_page.css";

import Header from "../../components/Header.jsx";
import Footer from "../../components/Footer.jsx";
import Slider from "../../components/Slider.jsx";
import ShortNewsCard from "../../components/ShortNewsCard";
import Pagination from "../../components/Pagination.jsx";
import useAutoScroll from "../../hooks/useAutoScroll.js";
import Loader from "../../components/Loader.jsx";
import ErrorPage from "../ErrorPage.jsx";

import ChevronRightIcon from "../../assets/icons/chevron-right.svg";

import { fetchShortNews, fetchDetailedNews } from "../../services/apiService.js";

import { IMAGE_BASE_URL } from "../../services/imageConfig.js";

const NewsPage = () => {
  const {
    data: shortNewsData,
    isLoading: isShortNewsLoading,
    error: shortNewsError,
  } = useQuery({ queryKey: ["shortNews"], queryFn: fetchShortNews });

  const {
    data: detailedNewsData,
    isLoading: isDetailedNewsLoading,
    error: detailedNewsError,
  } = useQuery({ queryKey: ["detailedNews"], queryFn: fetchDetailedNews });

  const [imagesLoaded, setImagesLoaded] = useState(false);
  const isLoading = isShortNewsLoading || isDetailedNewsLoading;

  const sliderImages = useMemo(() => {
    return detailedNewsData
      ? detailedNewsData.map((item) => ({
        id: item.id,
        url: IMAGE_BASE_URL + item.titleImage,
        date: item.date,
        title: item.title,
        description: item.description,
      }))
      : [];
  }, [detailedNewsData]);

  const newsItems = useMemo(() => {
    return shortNewsData
      ? shortNewsData.map((item) => ({
        id: item.id,
        image: IMAGE_BASE_URL + item.image,
        date: item.date,
        title: item.title,
      }))
      : [];
  }, [shortNewsData]);

  const [currentNewsPage, setCurrentNewsPage] = useState(1);
  const newsPerPage = 8;
  const indexOfLastNews = currentNewsPage * newsPerPage;
  const indexOfFirstNews = indexOfLastNews - newsPerPage;
  const currentNews = newsItems.slice(indexOfFirstNews, indexOfLastNews);
  const totalPages = Math.ceil(newsItems.length / newsPerPage);

  const {
    containerRef: newsContainerRef,
    handleMouseEnter,
    handleMouseLeave,
    handleTouchStart,
    handleTouchEnd,
  } = useAutoScroll({ speed: 2 });

  useEffect(() => {
    if (!isLoading && !shortNewsError && !detailedNewsError) {
      const imageUrls = [];
      sliderImages.forEach((item) => {
        if (item.url) imageUrls.push(item.url);
      });
      newsItems.forEach((item) => {
        if (item.image) imageUrls.push(item.image);
      });

      if (imageUrls.length === 0) {
        setImagesLoaded(true);
      } else {
        Promise.all(
          imageUrls.map(
            (url) =>
              new Promise((resolve) => {
                const img = new Image();
                img.src = url;
                img.onload = resolve;
                img.onerror = resolve;
              })
          )
        ).then(() => {
          setImagesLoaded(true);
        });
      }
    }
  }, [isLoading, shortNewsError, detailedNewsError, sliderImages, newsItems]);

  if (shortNewsError || detailedNewsError) {
    return <ErrorPage />;
  }

  if (isLoading || !imagesLoaded) {
    return (
      <div className="loader-wrapper">
        <Loader />
        <p className="loading-text">Загрузка...</p>
      </div>
    );
  }

  return (
    <div className="news-page">
      <Header />
      <nav className="page-nav">
        <a href="/" className="nav-link">Главная</a>
        <img src={ChevronRightIcon} alt="Chevron" className="nav-chevron-icon" />
        <a href="/about_company" className="nav-link">О компании</a>
        <img src={ChevronRightIcon} alt="Chevron" className="nav-chevron-icon" />
        <span className="nav-link">Новости</span>
      </nav>
      <div className="news-page-content">
        <div className="news-slider-section">
          {sliderImages.length > 0 ? (
            <Slider imagesData={sliderImages} styleType="small" contentType="news" />
          ) : null}
        </div>
        <div className="news-section">
          <div
            className="news-container"
            ref={newsContainerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="news-grid">
              {currentNews.length > 0 ? (
                currentNews.map((news) => (
                  <ShortNewsCard
                    key={news.id}
                    image={news.image}
                    date={news.date}
                    title={news.title}
                  />
                ))
              ) : null}
            </div>
          </div>
          <Pagination
            currentPage={currentNewsPage}
            totalPages={totalPages}
            onPageChange={setCurrentNewsPage}
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default NewsPage;
